import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Leaf, Lock, Loader2, CheckCircle2 } from "lucide-react";

export default function AdminResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError("Le mot de passe doit contenir au moins 8 caractères.");
      return;
    }
    if (password !== confirm) {
      setError("Les mots de passe ne correspondent pas.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      setTimeout(() => navigate("/login", { replace: true }), 2500);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(detail || "Le lien est invalide ou a expiré. Redemandez un nouveau lien.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[#F5F5F0] flex items-center justify-center p-4 relative overflow-hidden"
      data-testid="admin-reset-password-page"
    >
      <div className="absolute inset-0 bg-gradient-to-br from-[#F5F5F0] via-[#EFEEDF] to-[#E0E5DC]" aria-hidden="true" />
      <div className="absolute top-0 right-0 w-[60%] h-[60%] bg-[#1F4E3D]/5 rounded-full blur-3xl -translate-y-1/3 translate-x-1/4" aria-hidden="true" />
      <div className="absolute bottom-0 left-0 w-[40%] h-[40%] bg-[#8C6B2F]/5 rounded-full blur-3xl translate-y-1/3 -translate-x-1/4" aria-hidden="true" />

      <div className="relative w-full max-w-md">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <div className="w-11 h-11 rounded-lg bg-[#1F4E3D] flex items-center justify-center text-white shadow-sm">
            <Leaf className="w-5 h-5" />
          </div>
          <div>
            <div className="font-heading text-xl font-medium text-[#1A1C1A]">AgroManage</div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#575A57]">Production & Traçabilité</div>
          </div>
        </div>

        {done ? (
          <div
            className="bg-white border border-[#E5E5E0] rounded-xl p-7 shadow-[0_8px_30px_rgba(31,78,61,0.08)] text-center"
            data-testid="admin-reset-done"
          >
            <CheckCircle2 className="w-12 h-12 text-[#1F4E3D] mx-auto mb-3" />
            <h1 className="font-heading text-xl font-medium text-[#1A1C1A]">Mot de passe modifié</h1>
            <p className="text-sm text-[#575A57] mt-2">
              Votre nouveau mot de passe est actif. Redirection vers la
              connexion…
            </p>
          </div>
        ) : (
          <form
            onSubmit={onSubmit}
            className="bg-white border border-[#E5E5E0] rounded-xl p-7 shadow-[0_8px_30px_rgba(31,78,61,0.08)]"
            data-testid="admin-reset-form"
          >
            <h1 className="font-heading text-2xl font-medium text-[#1A1C1A]">Nouveau mot de passe</h1>
            <p className="text-sm text-[#575A57] mt-1 mb-6">
              Choisissez un nouveau mot de passe pour votre compte
              administrateur (8 caractères minimum).
            </p>

            <div className="space-y-4">
              <div>
                <Label htmlFor="new-password" className="mb-1.5 block text-[#1A1C1A]">Nouveau mot de passe</Label>
                <div className="relative">
                  <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#575A57] pointer-events-none" />
                  <Input
                    id="new-password"
                    type="password"
                    autoComplete="new-password"
                    minLength={8}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="pl-9"
                    data-testid="admin-reset-password"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="confirm-password" className="mb-1.5 block text-[#1A1C1A]">Confirmer le mot de passe</Label>
                <div className="relative">
                  <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#575A57] pointer-events-none" />
                  <Input
                    id="confirm-password"
                    type="password"
                    autoComplete="new-password"
                    minLength={8}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    className="pl-9"
                    data-testid="admin-reset-confirm"
                  />
                </div>
              </div>

              {error && (
                <div
                  className="bg-[#FCEEEA] border border-[#C05C47]/30 text-[#8B3D2D] text-sm rounded-md px-3 py-2"
                  data-testid="admin-reset-error"
                >
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={submitting || !password || !confirm}
                className="w-full bg-[#1F4E3D] hover:bg-[#173B2E] text-white"
                data-testid="admin-reset-submit"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enregistrement…</>
                ) : (
                  "Réinitialiser le mot de passe"
                )}
              </Button>
            </div>

            <div className="mt-6 text-center">
              <Link
                to="/login"
                className="text-sm underline text-[#575A57] hover:text-[#1F4E3D]"
                data-testid="admin-reset-back-login"
              >
                ← Retour à la connexion
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
